import { Component } from '@theme/component';

/**
 * Header Component
 * Handles mobile menu toggle and homepage scroll behavior
 */
class HeaderComponent extends Component {
  #abortController = new AbortController();
  #isMobileMenuOpen = false;
  #transparentScrollBehavior = this.classList.contains('header--transparent-scroll-behavior');
  #announcementBar = document.querySelector('.announcement-bar');
  #megaMenuTimeout = null;
  #activeMegaMenu = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setupEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Setup event listeners for header interactions
   */
  #setupEventListeners() {
    // Homepage and announcement bar scroll behavior
    if (this.#transparentScrollBehavior || this.#announcementBar) {
      this.#setupScrollBehavior();
    }
    
    // Mobile menu interactions
    this.#setupMobileMenuInteractions();
    
    // Mega menu hover gap handling
    this.#setupMegaMenuHoverGap();
    
    // Desktop search functionality
    this.#setupDesktopSearch();

    // Mobile search functionality
    this.#setupMobileSearch();
    
    // Close menus on escape key
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.#closeAllMenus();
      }
    }, {
      signal: this.#abortController.signal,
    });

    this.#setupMegaMenuFocusManagement();
  }

  /**
   * Setup homepage and announcement bar scroll behavior
   */
  #setupScrollBehavior() {
    const headerContainer = this.querySelector('.header__container');
    
    if (!headerContainer) return;

    let ticking = false;

    const updateHeaderState = () => {
      const currentScrollY = window.scrollY;
      
      if (this.#announcementBar) {
        const marginTop = Math.max(0, this.#announcementBar.offsetHeight - currentScrollY);
        this.style.setProperty('--header-overlay-offset-y', `${marginTop}px`);
      }
      
      if (this.#transparentScrollBehavior) {
        if (currentScrollY > 50) {
          headerContainer.setAttribute('data-scrolled', 'true');
        } else {
          headerContainer.removeAttribute('data-scrolled');
        }
      }
      
      ticking = false;
    };

    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(updateHeaderState);
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestTick, {
      signal: this.#abortController.signal,
    });

    // Initial state
    updateHeaderState();
  }

  /**
   * Setup mobile menu toggle interactions
   */
  #setupMobileMenuInteractions() {
    const mobileMenuToggle = this.querySelector('[data-mobile-menu-toggle]');
    
    if (!mobileMenuToggle) return;

    // Mobile menu toggle
    mobileMenuToggle.addEventListener('click', () => this.#toggleMobileMenu(), {
      signal: this.#abortController.signal,
    });

    // Close mobile menu when clicking outside
    document.addEventListener('click', (event) => {
      if (this.#isMobileMenuOpen && !this.contains(event.target instanceof Node ? event.target : null)) {
        this.#closeMobileMenu();
      }
    }, {
      signal: this.#abortController.signal,
    });

    // Setup mobile submenu interactions
    this.#setupMobileSubmenuInteractions();
  }

  /**
   * Setup mobile submenu interactions
   */
  #setupMobileSubmenuInteractions() {
    const submenuTriggers = this.querySelectorAll('[data-mobile-submenu-trigger]');
    const submenuDirectLinks = this.querySelectorAll('[data-mobile-link]');
    const backButtons = this.querySelectorAll('.mobile-submenu-back');

    // Handle submenu triggers
    submenuTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        const navItem = trigger.closest('.header__nav-item');
        if (!navItem) return;
        
        const submenu = navItem.querySelector('.mobile-submenu');
        if (submenu) {
          submenu.classList.add('active');
          trigger.setAttribute('aria-expanded', 'true');
        }
      }, {
        signal: this.#abortController.signal,
      });
    });

    // Handle back buttons
    backButtons.forEach(backButton => {
      backButton.addEventListener('click', () => {
        const submenu = backButton.closest('.mobile-submenu');
        if (!submenu || !submenu.parentElement) return;
        
        const trigger = submenu.parentElement.querySelector('[data-mobile-submenu-trigger]');
        submenu.classList.remove('active');
        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
        }
      }, {
        signal: this.#abortController.signal,
      });
    });

    submenuDirectLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        if (window.innerWidth < 1024) {
          e.stopPropagation();
          e.preventDefault();
          location.href = link.dataset.mobileLink;
        }
      });
    });
  }

  /**
   * Setup comprehensive focus management for mega menus
   * This ensures mega menus are properly hidden when focus moves to elements without mega menus
   */
  #setupMegaMenuFocusManagement() {
    // Get all focusable elements in the header
    const allFocusableElements = this.querySelectorAll(
      'a, button, input, [tabindex]:not([tabindex="-1"])'
    );

    allFocusableElements.forEach(element => {
      element.addEventListener('focus', () => {
        const isMegaMenuTrigger = element.hasAttribute('data-mega-menu-trigger');
        
        const isInsideActiveMegaMenu = this.#activeMegaMenu && this.#activeMegaMenu.contains(element);
        
        if (!isMegaMenuTrigger && !isInsideActiveMegaMenu) {
          // If this element is not a mega menu trigger and not inside the active mega menu, hide any open mega menus
          if (this.#activeMegaMenu) {
            this.#hideMegaMenu(this.#activeMegaMenu);
          }
        }
      }, {
        signal: this.#abortController.signal,
      });
    });
  }

  /**
   * Setup mega menu hover gap handling
   * This prevents the hover state from being lost when moving from header links to mega menu
  */
  #setupMegaMenuHoverGap() {
    const megaMenuTriggers = this.querySelectorAll('[data-mega-menu-trigger]');
    
    megaMenuTriggers.forEach(trigger => {
      const megaMenu = trigger.nextElementSibling;
      if (!megaMenu || !megaMenu.hasAttribute('data-mega-menu')) return;

      // Create an invisible bridge element to fill the gap
      const bridge = document.createElement('div');
      bridge.className = 'mega-menu-bridge absolute left-0 right-0 bg-transparent';
      bridge.style.cssText = `
        position: absolute;
        left: 0;
        right: 0;
        top: 100%;
        height: 39px;
        background: transparent;
        z-index: 999;
        pointer-events: auto;
      `;
      
      // Insert bridge after the trigger
       if (trigger.parentElement) {
         trigger.parentElement.insertBefore(bridge, megaMenu);
       }


      // Handle trigger hover
      trigger.addEventListener('mouseenter', () => {
        this.#clearMegaMenuTimeout();
        this.#showMegaMenu(megaMenu, trigger);
        
        // Close search expansion when hovering over mega menu trigger
        this.#closeSearchExpansion();
      }, {
        signal: this.#abortController.signal,
      });

      trigger.addEventListener('mouseleave', () => {
        this.#scheduleMegaMenuHide(megaMenu, trigger);
      }, {
        signal: this.#abortController.signal,
      });

      // Handle trigger focus for keyboard navigation
      trigger.addEventListener('focus', () => {
        this.#clearMegaMenuTimeout();
        this.#showMegaMenu(megaMenu, trigger);
        
        // Close search expansion when focusing on mega menu trigger
        this.#closeSearchExpansion();
      }, {
        signal: this.#abortController.signal,
      });

      trigger.addEventListener('blur', () => {
        // Only hide if focus is moving outside the entire header component
        setTimeout(() => {
          const activeElement = document.activeElement;
          if (!this.contains(activeElement)) {
            this.#scheduleMegaMenuHide(megaMenu, trigger);
          }
        }, 100); // Small delay to allow focus to move to mega menu items
      }, {
        signal: this.#abortController.signal,
      });

      // Handle bridge hover
      bridge.addEventListener('mouseenter', () => {
        this.#clearMegaMenuTimeout();
        this.#showMegaMenu(megaMenu, trigger);
      }, {
        signal: this.#abortController.signal,
      });

      bridge.addEventListener('mouseleave', () => {
        this.#scheduleMegaMenuHide(megaMenu, trigger);
      }, {
        signal: this.#abortController.signal,
      });

      // Handle mega menu hover
      megaMenu.addEventListener('mouseenter', () => {
        this.#clearMegaMenuTimeout();
        this.#showMegaMenu(megaMenu, trigger);
      }, {
        signal: this.#abortController.signal,
      });

      megaMenu.addEventListener('mouseleave', () => {
        this.#scheduleMegaMenuHide(megaMenu, trigger);
      }, {
        signal: this.#abortController.signal,
      });

      // Handle keyboard navigation within mega menu
      megaMenu.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.#hideMegaMenu(megaMenu);
          trigger.focus(); // Return focus to trigger
        }
      }, {
        signal: this.#abortController.signal,
      });
    });
  }

  /**
   * Show mega menu
   */
  #showMegaMenu(megaMenu, trigger) {
    // Hide any other active mega menu
    if (this.#activeMegaMenu && this.#activeMegaMenu !== megaMenu) {
      this.#hideMegaMenu(this.#activeMegaMenu);
    }

    this.#activeMegaMenu = megaMenu;
    megaMenu.classList.add('menu-active');
    trigger.setAttribute('aria-expanded', 'true');
    
    // Make mega menu focusable for keyboard navigation
    megaMenu.setAttribute('tabindex', '-1');
    
    let megamenuOpenClass = ['mega-menu--open'];
    
    const headerContainer = this.querySelector('.header__container');
    headerContainer && headerContainer.classList.add('mega-menu--open');
    
    // Show the dark gradient overlay
    const overlay = this.querySelector('.mega-menu-overlay');
    overlay && overlay.classList.add('active');
  }

  /**
   * Hide mega menu
   */
  #hideMegaMenu(megaMenu) {
    megaMenu.classList.remove('menu-active');
    
    const trigger = megaMenu.previousElementSibling?.previousElementSibling;
    if (trigger && trigger.hasAttribute('data-mega-menu-trigger')) {
      trigger.setAttribute('aria-expanded', 'false');
    }
    
    if (this.#activeMegaMenu === megaMenu) {
      this.#activeMegaMenu = null;
    }
    
    if (!this.#activeMegaMenu) {
      const headerContainer = this.querySelector('.header__container');
      headerContainer && headerContainer.classList.remove('mega-menu--open');
      
      // Hide the overlay
      const overlay = this.querySelector('.mega-menu-overlay');
      overlay && overlay.classList.remove('active');
    }
  }

  /**
   * Schedule mega menu hide with delay
   */
  #scheduleMegaMenuHide(megaMenu, trigger) {
    this.#clearMegaMenuTimeout();
    this.#megaMenuTimeout = window.setTimeout(() => {
      this.#hideMegaMenu(megaMenu);
    }, 150); // 150ms delay to allow smooth transition
  }

  /**
   * Clear mega menu timeout
   */
  #clearMegaMenuTimeout() {
    if (this.#megaMenuTimeout) {
      clearTimeout(this.#megaMenuTimeout);
      this.#megaMenuTimeout = null;
    }
  }

  /**
   * Setup desktop search functionality
   */
  #setupDesktopSearch() {
    const searchToggle = this.querySelector('[data-search-toggle]');
    const searchExpansion = this.querySelector('[data-search-expansion]');
    const searchInput = this.querySelector('[data-search-input]');
    const searchClose = this.querySelector('[data-search-close]');
    const headerContainer = this.querySelector('.header__container');

    if (!searchToggle || !searchExpansion || !searchInput || !searchClose) return;

    // Toggle search expansion
    searchToggle.addEventListener('click', () => {
      this.#toggleSearchExpansion();
    }, {
      signal: this.#abortController.signal,
    });

    // Close search
    searchClose.addEventListener('click', () => {
      this.#closeSearchExpansion();
    }, {
      signal: this.#abortController.signal,
    });

    // Handle search input
    searchInput.addEventListener('input', (e) => {
      const target = e.target;
      if (target && target instanceof HTMLInputElement) {
        this.#handleSearchInput(target.value);
      }
    }, {
      signal: this.#abortController.signal,
    });

    // Handle Enter key to submit search
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const searchTerm = searchInput.value.trim();
        if (searchTerm.length > 0) {
          window.location.href = `/search?q=${encodeURIComponent(searchTerm)}`;
        }
      }
    }, {
      signal: this.#abortController.signal,
    });

    // Close search on escape key
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && searchExpansion && searchExpansion.classList.contains('active')) {
        this.#closeSearchExpansion();
      }
    }, {
      signal: this.#abortController.signal,
    });

    // Load initial content
    this.#loadSearchContent();
  }

  /**
   * Toggle search expansion
   */
  #toggleSearchExpansion() {
    const searchExpansion = this.querySelector('[data-search-expansion]');
    const headerContainer = this.querySelector('.header__container');
    const searchInput = this.querySelector('[data-search-input]');

    if (searchExpansion && searchExpansion.classList.contains('active')) {
      this.#closeSearchExpansion('desktop');
    } else {
      this.#openSearchExpansion('desktop');
    }
  }

  /**
   * Open search expansion
   */
  #openSearchExpansion(context = 'desktop') {
    const searchExpansion = this.querySelector(context === 'mobile' ? '[data-mobile-search-expansion]' : '[data-search-expansion]');
    const headerContainer = this.querySelector('.header__container');
    const searchInput = this.querySelector(context === 'mobile' ? '[data-mobile-search-input]' : '[data-search-input]');
    const overlay = this.querySelector('.mega-menu-overlay');

    // Close any open mega menus first
    if (this.#activeMegaMenu) {
      this.#hideMegaMenu(this.#activeMegaMenu);
    }

    searchExpansion.classList.add('active');
    
    // Only add search--open class for desktop
    if (context === 'desktop' && headerContainer) {
      headerContainer.classList.add('search--open');
    }
    
    // Activate the mega menu overlay for search (desktop only)
    if (overlay && context === 'desktop') {
      overlay.classList.add('active');
    }
    
    // Show mobile search close button
    if (context === 'mobile') {
      const mobileSearchClose = this.querySelector('[data-mobile-search-close]');
      if (mobileSearchClose) {
        mobileSearchClose.classList.remove('hidden');
      }
    }
    
    searchInput.focus();

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('search:open', {
      bubbles: true,
      detail: { element: this, context }
    }));
  }

  /**
   * Close search expansion
   */
  #closeSearchExpansion(context = 'desktop') {
    const searchExpansion = this.querySelector(context === 'mobile' ? '[data-mobile-search-expansion]' : '[data-search-expansion]');
    const headerContainer = this.querySelector('.header__container');
    const searchInput = this.querySelector(context === 'mobile' ? '[data-mobile-search-input]' : '[data-search-input]');
    const overlay = this.querySelector('.mega-menu-overlay');

    searchExpansion.classList.remove('active');
    
    // Only remove search--open class for desktop
    if (context === 'desktop' && headerContainer) {
      headerContainer.classList.remove('search--open');
    }
    
    // Deactivate the mega menu overlay when search closes (desktop only)
    if (overlay && context === 'desktop') {
      overlay.classList.remove('active');
    }
    
    // Hide mobile search close button
    if (context === 'mobile') {
      const mobileSearchClose = this.querySelector('[data-mobile-search-close]');
      if (mobileSearchClose) {
        mobileSearchClose.classList.add('hidden');
      }
    }
    
    searchInput.value = '';

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('search:close', {
      bubbles: true,
      detail: { element: this, context }
    }));
  }

  /**
   * Handle search input
   */
  #handleSearchInput(value, context = 'desktop') {
    const searchTerm = value.trim();
    
    // Update the "View All Results" link href and visibility
    if (context === 'desktop') {
      const viewAllResultsLink = this.querySelector('[data-view-all-results]');
      if (viewAllResultsLink) {
        viewAllResultsLink.href = searchTerm.length > 0 ? `/search?q=${encodeURIComponent(searchTerm)}` : '/search';
        // Hide "View All Results" link when showing popular searches, show when showing suggestions
        viewAllResultsLink.style.display = searchTerm.length > 0 ? 'inline-flex' : 'none';
      }
    } else if (context === 'mobile') {
      const mobileViewAllResultsLink = this.querySelector('[data-mobile-view-all-results]');
      if (mobileViewAllResultsLink) {
        mobileViewAllResultsLink.href = searchTerm.length > 0 ? `/search?q=${encodeURIComponent(searchTerm)}` : '/search';
        // Hide "View All Results" link when showing popular searches, show when showing suggestions
        mobileViewAllResultsLink.style.display = searchTerm.length > 0 ? 'inline-flex' : 'none';
      }
    }
    
    if (searchTerm.length > 0) {
      this.#performPredictiveSearch(searchTerm, context);
      this.#updateSearchHeadlines(true, context); // Show "Suggestions" and "Products"
      if (context === 'desktop') {
        this.#updateSearchButtons(true); // Show submit button, hide close button (desktop only)
      }
    } else {
      this.#loadSearchContent(context); // Show default content when input is empty
      this.#updateSearchHeadlines(false, context); // Show "Popular Searches" and "Trending Products"
      if (context === 'desktop') {
        this.#updateSearchButtons(false); // Show close button, hide submit button (desktop only)
      }
    }
  }

  /**
   * Load search content (popular searches and trending products)
   */
  async #loadSearchContent(context = 'desktop') {
    await this.#loadPopularSearches(context);
    await this.#loadTrendingProducts(context);
    this.#updateSearchHeadlines(false, context); // Show "Popular Searches" and "Trending Products"
    
    // Hide "View All Results" link when showing initial content
    if (context === 'desktop') {
      const viewAllResultsLink = this.querySelector('[data-view-all-results]');
      if (viewAllResultsLink) {
        viewAllResultsLink.style.display = 'none';
      }
    } else if (context === 'mobile') {
      const mobileViewAllResultsLink = this.querySelector('[data-mobile-view-all-results]');
      if (mobileViewAllResultsLink) {
        mobileViewAllResultsLink.style.display = 'none';
      }
    }
  }

  /**
   * Update search headlines based on search state
   */
  #updateSearchHeadlines(isSearching, context = 'desktop') {
    const popularSearchesTitle = this.querySelector(context === 'mobile' ? '[data-mobile-popular-searches-title]' : '[data-popular-searches-title]');
    const trendingProductsTitle = this.querySelector(context === 'mobile' ? '[data-mobile-trending-products-title]' : '[data-trending-products-title]');

    if (popularSearchesTitle) {
      popularSearchesTitle.textContent = isSearching ? 'Suggestions' : 'Popular Searches';
    }

    if (trendingProductsTitle) {
      trendingProductsTitle.textContent = isSearching ? 'Products' : 'Trending Products';
    }
  }

  /**
   * Update search buttons based on search state
   */
  #updateSearchButtons(isSearching) {
    const closeButton = this.querySelector('[data-search-close]');
    const submitButton = this.querySelector('[data-search-submit]');

    if (closeButton) {
      closeButton.classList.toggle('hidden', isSearching);
    }

    if (submitButton) {
      submitButton.classList.toggle('hidden', !isSearching);
    }
  }

  /**
   * Load popular searches
   */
  async #loadPopularSearches(context = 'desktop') {
    const popularSearchesContainer = this.querySelector(context === 'mobile' ? '[data-mobile-popular-searches]' : '[data-popular-searches]');
    if (!popularSearchesContainer) return;

    // Show the specific popular searches for the initial state (5 searches, no "View All Results" link)
    const defaultSearches = [
      'Alexander Valley Cabernet',
      'Napa Valley Cabernet',
      'Release Day',
      'Gifts',
      'Events'
    ];

    popularSearchesContainer.innerHTML = defaultSearches.map(search => `
      <a href="/search?q=${encodeURIComponent(search)}" class="text-[var(--megamenu-foreground-color)] hover:text-gray-300 transition-colors font-['Adobe_Garamond_Pro'] text-[18px] mobile:text-[18px] desktop:text-[24px] font-normal leading-[120%] mobile:tracking-[0px] desktop:tracking-[-0.48px]">
        ${search}
      </a>
    `).join('');
  }

  /**
   * Load trending products
   */
  async #loadTrendingProducts(context = 'desktop') {
    const trendingProductsContainer = this.querySelector(context === 'mobile' ? '[data-mobile-trending-products]' : '[data-trending-products]');
    if (!trendingProductsContainer) return;

    try {
      // Fetch products using the predictive search API
      const url = new URL(Theme.routes.predictive_search_url, location.origin);
      url.searchParams.set('q', '*');
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '4');
      url.searchParams.set('resources[limit_scope]', 'each');
      
      const jsonUrl = url.toString().replace('/search/suggest', '/search/suggest.json');
      
      const response = await fetch(jsonUrl);
      const data = await response.json();
      
      if (data.resources?.results?.products?.length > 0) {
        const products = data.resources.results.products.slice(0, 4); // Max 4 products
        
        trendingProductsContainer.innerHTML = products.map(product => {
          // Split wine title if it contains a year
          const splitTitle = this.#splitWineTitle(product.title);
          
          return `
            <div class="w-full mobile:w-full desktop:w-1/4">
              <a href="${product.url}" class="block h-full bg-[var(--color-smoke)] text-[var(--color-rich-black)] no-underline">
                <div class="flex flex-col">
                  <div class="max-h-[30vh] overflow-hidden">
                    <img src="${product.image}" alt="${product.title}" class="w-full h-full object-cover" loading="lazy">
                  </div>
                  <div class="p-4 flex flex-col gap-2 text-center">
                    <h3 class="font-['Adobe_Garamond_Pro'] text-[14px] mobile:text-[14px] desktop:text-[16px] font-normal leading-[135%] mobile:tracking-[0.14px] desktop:tracking-[0px] flex flex-col gap-[3px] lg:gap-[4px]">${splitTitle}</h3>
                    <div class="font-['Adobe_Garamond_Pro'] text-[14px] mobile:text-[14px] desktop:text-[16px] font-normal leading-[135%] mobile:tracking-[0.14px] desktop:tracking-[0px]">$${product.price}</div>
                  </div>
                </div>
              </a>
            </div>
          `;
        }).join('');
      } else {
        trendingProductsContainer.innerHTML = '<p class="text-[var(--megamenu-foreground-color)]">No trending products available</p>';
      }
    } catch (error) {
      console.error('Error loading trending products:', error);
      trendingProductsContainer.innerHTML = '<p class="text-[var(--megamenu-foreground-color)]">Unable to load trending products</p>';
    }
  }

  /**
   * Perform predictive search
   */
  async #performPredictiveSearch(searchTerm, context = 'desktop') {
    const popularSearchesContainer = this.querySelector(context === 'mobile' ? '[data-mobile-popular-searches]' : '[data-popular-searches]');
    const trendingProductsContainer = this.querySelector(context === 'mobile' ? '[data-mobile-trending-products]' : '[data-trending-products]');
    
    if (!popularSearchesContainer || !trendingProductsContainer) return;

    try {
      const url = new URL(Theme.routes.predictive_search_url, location.origin);
      url.searchParams.set('q', searchTerm);
      url.searchParams.set('resources[type]', 'query,product');
      url.searchParams.set('resources[limit]', '5');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('section_id', 'predictive-search');

      const response = await fetch(url);
      const html = await response.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Update popular searches with query suggestions (4 suggestions + "View All Results" link)
      const queryCards = doc.querySelectorAll('.predictive-search-results__card--query');
      if (queryCards.length > 0) {
        const suggestions = Array.from(queryCards).slice(0, 4).map(card => {
          const link = card.querySelector('a');
          const text = card.textContent?.trim() || '';
          return { url: link?.href || '', text };
        });
        
        popularSearchesContainer.innerHTML = suggestions.map(suggestion => `
          <a href="${suggestion.url}" class="text-[var(--megamenu-foreground-color)] hover:text-gray-300 transition-colors font-['Adobe_Garamond_Pro'] text-[18px] mobile:text-[18px] desktop:text-[24px] font-normal leading-[120%] mobile:tracking-[0px] desktop:tracking-[-0.48px]">
            ${suggestion.text}
          </a>
        `).join('');
      } else {
        popularSearchesContainer.innerHTML = '<p class="text-[var(--megamenu-foreground-color)] col-start-1 col-end-3">Sorry we couldn\'t find anything that matches your search</p>';
      }
      
      // Update trending products with search results
      const productCards = doc.querySelectorAll('.predictive-search-results__card--product');
      if (productCards.length > 0) {
        // Extract product data and render products card structure (max 4 products)
        const products = Array.from(productCards).slice(0, 4).map(card => {
          const link = card.querySelector('a');
          const title = card.querySelector('.resource-card__title')?.textContent || '';
          const price = card.querySelector('.price')?.textContent || '';
          const image = card.querySelector('img')?.src || '';
          
          return { url: link?.href || '', title, price, image };
        });
        
        trendingProductsContainer.innerHTML = products.map(product => {
          // Split wine title if it contains a year
          const splitTitle = this.#splitWineTitle(product.title);
          
          return `
            <div class="w-full mobile:w-full desktop:w-1/4">
              <a href="${product.url}" class="block h-full bg-[var(--color-smoke)] text-[var(--color-rich-black)] no-underline">
                <div class="flex flex-col">
                  <div class="max-h-[30vh] overflow-hidden">
                    <img src="${product.image}" alt="${product.title}" class="w-full h-full object-cover" loading="lazy">
                  </div>
                  <div class="p-4 flex flex-col gap-2 text-center">
                    <h3 class="font-['Adobe_Garamond_Pro'] text-[14px] mobile:text-[14px] desktop:text-[16px] font-normal leading-[135%] mobile:tracking-[0.14px] desktop:tracking-[0px] flex flex-col gap-[3px] lg:gap-[4px]">${splitTitle}</h3>
                    <div class="font-['Adobe_Garamond_Pro'] text-[14px] mobile:text-[14px] desktop:text-[16px] font-normal leading-[135%] mobile:tracking-[0.14px] desktop:tracking-[0px]">${product.price}</div>
                  </div>
                </div>
              </a>
            </div>
          `;
        }).join('');
      } else {
        trendingProductsContainer.innerHTML = '<p class="text-[var(--megamenu-foreground-color)] col-start-1 col-end-3">Sorry we couldn\'t find anything that matches your search</p>';
      }
    } catch (error) {
      console.error('Error performing predictive search:', error);
      popularSearchesContainer.innerHTML = '<p class="text-gray-500">Unable to load search suggestions</p>';
      trendingProductsContainer.innerHTML = '<p class="text-gray-500">Unable to load products</p>';
    }
  }

  /**
   * Split wine title by pipe character
   * @param {string} title - The wine title to split
   * @returns {string} - HTML with spans for year and title parts
   */
  #splitWineTitle(title) {
    const titleParts = title.split('|');
    
    if (titleParts.length > 1) {
      return titleParts.map(part => `<span>${part.trim()}</span>`).join('');
    } else {
      return `<span>${title}</span>`;
    }
  }

  /**
   * Toggle mobile menu
   */
  #toggleMobileMenu() {
    if (this.#isMobileMenuOpen) {
      this.#closeMobileMenu();
    } else {
      this.#openMobileMenu();
    }
  }

  /**
   * Open mobile menu
   */
  #openMobileMenu() {
    this.#isMobileMenuOpen = true;

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Add mobile menu open class to header component
    this.classList.add('mobile-menu--open');

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('mobile-menu:open', {
      bubbles: true,
      detail: { element: this }
    }));
  }

  /**
   * Close mobile menu
   */
  #closeMobileMenu() {
    this.#isMobileMenuOpen = false;

    // Restore body scroll
    document.body.style.overflow = '';

    // Remove mobile menu open class from header component
    this.classList.remove('mobile-menu--open');

    // Close mobile search expansion if it's open
    const mobileSearchExpansion = this.querySelector('[data-mobile-search-expansion]');
    if (mobileSearchExpansion && mobileSearchExpansion.classList.contains('active')) {
      this.#closeSearchExpansion('mobile');
    }

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('mobile-menu:close', {
      bubbles: true,
      detail: { element: this }
    }));
  }

  /**
   * Close all menus
   */
  #closeAllMenus() {
    if (this.#isMobileMenuOpen) {
      this.#closeMobileMenu();
    }
    
    // Close any open mega menus
    if (this.#activeMegaMenu) {
      this.#hideMegaMenu(this.#activeMegaMenu);
    }
    
    // Close search expansion
    this.#closeSearchExpansion();
    
    this.#clearMegaMenuTimeout();
  }

  /**
   * Public method to toggle mobile menu programmatically
   */
  toggleMobileMenu() {
    this.#toggleMobileMenu();
  }

  /**
   * Public method to open mobile menu programmatically
   */
  openMobileMenu() {
    this.#openMobileMenu();
  }

  /**
   * Public method to close mobile menu programmatically
   */
  closeMobileMenu() {
    this.#closeMobileMenu();
  }

  /**
   * Setup mobile search functionality (reuses desktop methods)
   */
  #setupMobileSearch() {
    const mobileSearchInput = this.querySelector('[data-mobile-search-input]');
    const mobileSearchExpansion = this.querySelector('[data-mobile-search-expansion]');
    
    if (!mobileSearchInput || !mobileSearchExpansion) return;

    // Handle mobile search input focus
    mobileSearchInput.addEventListener('focus', () => {
      this.#openSearchExpansion('mobile');
    }, {
      signal: this.#abortController.signal,
    });

    // Handle mobile search input
    mobileSearchInput.addEventListener('input', (e) => {
      const target = e.target;
      if (target && target instanceof HTMLInputElement) {
        this.#handleSearchInput(target.value, 'mobile');
      }
    }, {
      signal: this.#abortController.signal,
    });

    // Handle Enter key to submit search
    mobileSearchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const searchTerm = mobileSearchInput.value.trim();
        if (searchTerm.length > 0) {
          window.location.href = `/search?q=${encodeURIComponent(searchTerm)}`;
        }
      }
    }, {
      signal: this.#abortController.signal,
    });

    // Close mobile search on escape key
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && mobileSearchExpansion && mobileSearchExpansion.classList.contains('active')) {
        this.#closeSearchExpansion('mobile');
      }
    }, {
      signal: this.#abortController.signal,
    });

    // Handle mobile search close button
    const mobileSearchClose = this.querySelector('[data-mobile-search-close]');
    if (mobileSearchClose) {
      mobileSearchClose.addEventListener('click', () => {
        this.#closeSearchExpansion('mobile');
      }, {
        signal: this.#abortController.signal,
      });
    }

    // Load initial mobile search content
    this.#loadSearchContent('mobile');
  }

  /**
   * Need a new function to open minicart here 
   * because the minicart trigger button was moved out of the cart-drawer.liquid 
   */
  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
  }

}

// Register the custom element
if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

export default HeaderComponent;
